/**
 * Minimal path router for the Corpus Reader (Task 189). The app has no router
 * dependency and is otherwise a boolean idle/chat view; this hook adds just
 * enough History-API routing to support deep-linkable `/corpus` URLs + anchors
 * (so Task 190 citations can link to an exact section).
 *
 * Routes:
 *   /                       → chat (view: "chat")
 *   /corpus                 → library index (view: "corpus", no book/chapter)
 *   /corpus/:book           → book (chapter list)
 *   /corpus/:book/:chapter  → chapter reader (+ optional #anchor)
 *   /corpus/flechas          → flat FAQ index
 *   /corpus/flechas/:videoId → one canonical Short (+ optional #anchor)
 *   /conexiones             → connections graph landing (view: "connections")
 *   /conexiones/:node       → graph focused on a node id (URL-encoded, Task 216)
 *   /caminos                → Caminos de Luz landing (view: "caminos"; the old
 *                             /sendas name is fully retired — Task 231)
 *   /caminos/:id            → a camino in flight mode at its first stop
 *   /caminos/:id/:stop      → flight mode at a specific stop (deep link)
 *   /caminos/trazo?from&to  → El Hilo Escarlata: an ephemeral traced flight over
 *                             the affirmed path between two passages (Task 226)
 *   /admin                  → operator analytics dashboard (view: "admin"). NOT
 *                             in any public nav/sitemap; the auth boundary is
 *                             Caddy Basic Auth + the proxy loopback bind. Only
 *                             the SPA-served /admin* paths reach here — Caddy
 *                             routes /admin/api/* to the proxy, never the SPA.
 *
 * Vite's dev server and `vite preview` do SPA history-fallback by default, so
 * path routing works locally (Diego's primary use). The query string (e.g.
 * ?lang=en) is preserved across navigation.
 */
import { useCallback, useEffect, useState } from "react";

export interface CorpusRoute {
  view: "chat" | "corpus" | "connections" | "caminos" | "admin";
  book: string | null;
  chapter: number | null;
  /** True for the dedicated `/corpus/flechas` FAQ surface. */
  shorts: boolean;
  /** Canonical YouTube id on a Flechas FAQ route; never a chapter surrogate. */
  shortVideoId: string | null;
  anchor: string | null;
  /** Task 216: focused graph node id on /conexiones/:node (decoded), else null. */
  node: string | null;
  /** Task 225/231: camino slug on /caminos/:id (decoded), else null. "trazo" =
   *  a Task 226 traced flight, with traceFrom/traceTo carrying the endpoints. */
  camino: string | null;
  /** Task 225: stop index on /caminos/:id/:stop, else null (= first stop). */
  stop: number | null;
  /** Task 226: ?from / ?to node ids on /caminos/trazo (decoded), else null. */
  traceFrom: string | null;
  traceTo: string | null;
}

const EMPTY = {
  book: null,
  chapter: null,
  shorts: false,
  shortVideoId: null,
  anchor: null,
  node: null,
  camino: null,
  stop: null,
  traceFrom: null,
  traceTo: null,
} as const;

/** Decode a single path segment without allowing a malformed external URL to
 * crash the SPA. `decodeURIComponent` throws URIError for incomplete escapes. */
function decodePathSegment(segment: string | undefined): string | null {
  if (segment === undefined) return null;
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
}

/** Exported for hermetic route smokes (Task 216). `search` (the `?...` string)
 *  is optional so existing callers keep working; Task 226 needs ?from/?to. */
export function parse(pathname: string, hash: string, search = ""): CorpusRoute {
  const segments = pathname.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);
  const anchor = hash.startsWith("#") ? hash.slice(1) : null;
  if (segments[0] === "admin") {
    // Any SPA-served /admin* path renders the operator dashboard. (Caddy routes
    // /admin/api/* to the proxy, so those never reach the SPA router.)
    return { view: "admin", ...EMPTY, anchor };
  }
  if (segments[0] === "conexiones") {
    const nodeRaw = segments[1];
    return {
      view: "connections",
      ...EMPTY,
      anchor,
      node: decodePathSegment(nodeRaw),
    };
  }
  if (segments[0] === "caminos") {
    const caminoRaw = segments[1];
    const stopRaw = segments[2];
    const params = new URLSearchParams(search);
    const from = params.get("from");
    const to = params.get("to");
    return {
      view: "caminos",
      ...EMPTY,
      anchor,
      camino: decodePathSegment(caminoRaw),
      stop: stopRaw !== undefined && /^\d+$/.test(stopRaw) ? Number.parseInt(stopRaw, 10) : null,
      traceFrom: from !== null && from.length > 0 ? from : null,
      traceTo: to !== null && to.length > 0 ? to : null,
    };
  }
  if (segments[0] !== "corpus") return { view: "chat", ...EMPTY };
  if (segments[1] === "flechas") {
    return {
      view: "corpus",
      ...EMPTY,
      shorts: true,
      shortVideoId: decodePathSegment(segments[2]),
      anchor,
    };
  }
  const book = segments[1] ?? null;
  const chapterRaw = segments[2];
  const chapter = chapterRaw !== undefined && /^\d+$/.test(chapterRaw) ? Number.parseInt(chapterRaw, 10) : null;
  return { view: "corpus", ...EMPTY, book: book ? decodePathSegment(book) : null, chapter, anchor };
}

/** The current query string with the transient trazo endpoints (from/to)
 *  STRIPPED — keeps lang and any share params, but stops the Task 226 trace
 *  params from leaking onto non-trace paths (e.g. /caminos/melquisedec?from=…).
 *  trazoPath re-adds them deliberately. (CTO review, 2026-06-13.) */
function cleanSearch(): string {
  if (typeof window === "undefined") return "";
  const p = new URLSearchParams(window.location.search);
  p.delete("from");
  p.delete("to");
  // Host-embed transport params, never user preferences (unlike `lang`, which
  // is preserved). In embed mode every link these helpers build BREAKS OUT to a
  // new tab — in-frame navigation is disabled — so carrying `embed` along would
  // open the full app chromeless in a real tab, with no nav and no way back.
  p.delete("embed");
  p.delete("send");
  const s = p.toString();
  return s ? `?${s}` : "";
}

/** Build a corpus path, preserving safe query params (lang). */
export function corpusPath(book?: string | null, chapter?: number | null, anchor?: string | null): string {
  let path = "/corpus";
  if (book) path += `/${encodeURIComponent(book)}`;
  if (book && typeof chapter === "number") path += `/${chapter}`;
  return `${path}${cleanSearch()}${anchor ? `#${anchor}` : ""}`;
}

/** Build a canonical FAQ path without pretending the video id is a chapter. */
export function corpusShortPath(videoId?: string | null, anchor?: string | null): string {
  const path = `/corpus/flechas${videoId ? `/${encodeURIComponent(videoId)}` : ""}`;
  return `${path}${cleanSearch()}${anchor ? `#${anchor}` : ""}`;
}

export function chatPath(): string {
  return `/${cleanSearch()}`;
}

/** Operator dashboard path. Entry is normally a full navigation/bookmark (so the
 *  native Basic Auth prompt fires), not an in-app soft nav — kept for the "volver"
 *  affordance and tests. */
export function adminPath(): string {
  return `/admin${cleanSearch()}`;
}

/** Task 216: build a connections-graph path, preserving safe query params. */
export function connectionsPath(node?: string | null): string {
  return `/conexiones${node ? `/${encodeURIComponent(node)}` : ""}${cleanSearch()}`;
}

/** Task 231: build a Caminos path (/caminos canonical), preserving safe query
 *  params (NOT the trazo from/to — those belong only to /caminos/trazo, via
 *  trazoPath). */
export function caminosPath(camino?: string | null, stop?: number | null): string {
  let path = "/caminos";
  if (camino) {
    path += `/${encodeURIComponent(camino)}`;
    if (typeof stop === "number") path += `/${stop}`;
  }
  return `${path}${cleanSearch()}`;
}

/** Task 226: build a traced-flight URL (/caminos/trazo?from&to). Keeps safe
 *  params (lang) and sets fresh from/to — shareable, the trace is URL-derived. */
export function trazoPath(from: string, to: string, stop?: number | null): string {
  const params = new URLSearchParams(cleanSearch());
  params.set("from", from);
  params.set("to", to);
  const path = typeof stop === "number" ? `/caminos/trazo/${stop}` : "/caminos/trazo";
  return `${path}?${params.toString()}`;
}

/**
 * SPA navigation usable from anywhere (e.g. a source card deep in the tree)
 * without prop-drilling a navigate callback: push the URL and emit popstate so
 * useCorpusRoute's listener updates the route. Task 190.
 */
export function softNavigate(to: string): void {
  if (typeof window === "undefined") return;
  window.history.pushState(null, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

/**
 * Task 190: build the Corpus Reader deep-link for a citation from the RAG-emitted
 * reader_anchor + book/chapter. Returns null when any piece is missing (the
 * reader link is then hidden — no fuzzy matching).
 */
export function citationReaderUrl(c: {
  reader_anchor?: unknown;
  content_kind?: unknown;
  video_id?: unknown;
  book?: unknown;
  chapter?: unknown;
}): string | null {
  if (typeof c.reader_anchor !== "string" || c.reader_anchor.length === 0) return null;
  if (c.content_kind === "short") {
    if (typeof c.video_id !== "string" || !/^[A-Za-z0-9_-]{11}$/.test(c.video_id)) return null;
    return `/corpus/flechas/${encodeURIComponent(c.video_id)}#${c.reader_anchor}`;
  }
  if (typeof c.book !== "string" || c.book.length === 0) return null;
  if (typeof c.chapter !== "number" || !Number.isFinite(c.chapter)) return null;
  return `/corpus/${encodeURIComponent(c.book)}/${c.chapter}#${c.reader_anchor}`;
}

export function useCorpusRoute(): { route: CorpusRoute; navigate: (to: string) => void } {
  const [route, setRoute] = useState<CorpusRoute>(() =>
    typeof window === "undefined"
      ? { view: "chat", ...EMPTY }
      : parse(window.location.pathname, window.location.hash, window.location.search),
  );

  useEffect(() => {
    const onPop = (): void =>
      setRoute(parse(window.location.pathname, window.location.hash, window.location.search));
    window.addEventListener("popstate", onPop);
    window.addEventListener("hashchange", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("hashchange", onPop);
    };
  }, []);

  const navigate = useCallback((to: string): void => {
    if (typeof window === "undefined") return;
    window.history.pushState(null, "", to);
    setRoute(parse(window.location.pathname, window.location.hash, window.location.search));
    // Jump to top on a route change unless an anchor was requested.
    if (!to.includes("#")) window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return { route, navigate };
}
